import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Product, UnifiedSearchResults } from '../types/products';
import { useTheme } from '../components/ThemeContext';
import { useGlobalFilters, COO_TIER_MAP } from '../components/GlobalFilterContext';
import { API_BASE } from '../../config';

// ============================================================
// TYPES
// ============================================================
type SortField = 'name' | 'risk' | 'manufacturer' | 'updated';
type SortDir = 'asc' | 'desc';
type ViewMode = 'table' | 'card';

interface Filters {
  risk: string;
  baa: string;
  coo: string;
  manufacturer: string;
  tiers: string[];
  shortage: string;
}

interface TierInfo {
  tier: string;
  count: number;
}

interface ProductsPageProps {
  loading: boolean;
  products: Product[];
  productsTotal: number;
  unifiedSearch: UnifiedSearchResults | null;
  unifiedSearchQuery: string;
  isUnifiedSearching: boolean;
  collapsedSections: Record<string, boolean>;
  toggleSection: (key: 'clinical_drugs' | 'products' | 'packagings') => void;
  setUnifiedSearchQuery: (value: string) => void;
  setProductFilter: (value: string) => void;
  setUnifiedSearch: (value: UnifiedSearchResults | null) => void;
  loadProductDetail: (id: number) => void;
  loadClinicalDrugDetail: (id: number) => void;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  IconSearch: React.ComponentType;
  IconSpinner: React.ComponentType;
  IconChevron: React.ComponentType;
  RiskBadge: React.ComponentType<{ risk: string }>;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
  StatCard: React.ComponentType<{ title: string; value: string | number; subtitle?: string; color?: string; icon?: React.ReactNode; accent?: string }>;
  IconDatabase: React.ComponentType;
  IconShield: React.ComponentType;
  IconAlert: React.ComponentType;
  IconFlag: React.ComponentType;
}

// ============================================================
// HELPER: COO Tier badge
// ============================================================
function normalizeCooTier(tier: string): { label: string; bg: string; fg: string } {
  const upper = tier.toUpperCase();
  if (upper === 'HIGH' || upper === 'NEAR_CERTAIN') {
    return { label: 'High', bg: '#22C55E18', fg: '#22C55E' };
  }
  if (upper === 'MODERATE' || upper === 'MODERATE_HIGH') {
    return { label: 'Medium', bg: '#F59E0B18', fg: '#F59E0B' };
  }
  return { label: 'Low', bg: '#EF444418', fg: '#EF4444' };
}

// ============================================================
// HELPER: Shortage status badge
// ============================================================
const ShortageBadge = ({ status, T }: { status: string; T: Record<string, any> }) => {
  const isActive = status && status !== 'None' && status !== 'none';
  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "3px 10px",
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      background: isActive ? T.warningBg : T.successBg,
      color: isActive ? T.warning : T.success,
      border: `1px solid ${isActive ? "#FDE68A" : "#BBF7D0"}`,
    }}>
      {isActive ? status : "None"}
    </span>
  );
};

// ============================================================
// HELPER: useMediaQuery hook
// ============================================================
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

// ============================================================
// MAIN COMPONENT
// ============================================================
export default function ProductsPage({
  loading,
  products,
  productsTotal,
  unifiedSearch,
  unifiedSearchQuery,
  isUnifiedSearching,
  collapsedSections,
  toggleSection,
  setUnifiedSearchQuery,
  setProductFilter,
  setUnifiedSearch,
  loadProductDetail,
  loadClinicalDrugDetail,
  SectionHeader,
  IconSearch,
  IconSpinner,
  IconChevron,
  RiskBadge,
  ScoreRing,
  StatCard,
  IconDatabase,
  IconShield,
  IconAlert,
  IconFlag,
}: ProductsPageProps) {
  const { T } = useTheme();
  const location = useLocation();
  const navTo = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { filters: globalFilters, setFilter: setGlobalFilter } = useGlobalFilters();

  // Read initial COO filter from URL query params (e.g. ?coo=India from Country Origins page)
  const initialCoo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('coo') || '';
  }, []);

  // Clear the URL query param after reading it so back-navigation doesn't re-apply stale filters
  useEffect(() => {
    if (initialCoo) {
      navTo('/products', { replace: true });
    }
  }, [initialCoo, navTo]);

  // Helper: derive local Filters from global filters
  const deriveFiltersFromGlobal = useCallback((): Filters => {
    const risk = globalFilters.riskLevel.length > 0 ? globalFilters.riskLevel[0] : '';
    const baa = globalFilters.baaEligible || '';
    // Pass ALL selected countries as comma-separated string for multi-select
    let coo = '';
    if (globalFilters.country.length > 0) {
      coo = globalFilters.country.join(',');
    } else if (initialCoo) {
      coo = initialCoo;
    }
    // Pass ALL selected manufacturers as comma-separated string for multi-select
    const manufacturer = globalFilters.manufacturer.length > 0
      ? globalFilters.manufacturer.join(',')
      : '';
    const tiers: string[] = [];
    globalFilters.cooConfidence.forEach(group => {
      const mapped = COO_TIER_MAP[group];
      if (mapped) tiers.push(...mapped);
    });
    const shortage = globalFilters.shortageStatus || '';
    return { risk, baa, coo, manufacturer, tiers, shortage };
  }, [globalFilters.riskLevel, globalFilters.baaEligible, globalFilters.country, globalFilters.manufacturer, globalFilters.cooConfidence, globalFilters.shortageStatus, initialCoo]);

  // Local state for filters — initialized from global filters so page loads with correct data
  const [filters, setFilters] = useState<Filters>(deriveFiltersFromGlobal);

  // Sync local filters from global filters when they change
  useEffect(() => {
    setFilters(deriveFiltersFromGlobal());
  }, [deriveFiltersFromGlobal]);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [viewMode, setViewMode] = useState<ViewMode>(isMobile ? 'card' : 'table');
  const [viewOverridden, setViewOverridden] = useState(false);

  // Server-side paginated data (A3.7)
  const [serverProducts, setServerProducts] = useState<Product[]>([]);
  const [serverTotal, setServerTotal] = useState(productsTotal);
  const [serverAggregates, setServerAggregates] = useState<{
    baa_eligible_count: number;
    high_risk_count: number;
    avg_mia_score: number;
    mia_confident_count: number;
    coo_confident_count: number;
    coo_high_count: number;
    coo_moderate_count: number;
    coo_low_count: number;
    shortage_product_count: number;
  }>({ baa_eligible_count: 0, high_risk_count: 0, avg_mia_score: 0, mia_confident_count: 0, coo_confident_count: 0, coo_high_count: 0, coo_moderate_count: 0, coo_low_count: 0, shortage_product_count: 0 });
  const [serverLoading, setServerLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Fetch coo_confident_distribution from dashboard stats for MIA tile
  const [cooConfidentDist, setCooConfidentDist] = useState<{ us: number; total: number }>({ us: 0, total: 0 });
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/metasupply/dashboard/stats`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          const dist: Record<string, number> = data.coo_confident_distribution || {};
          let us = 0, total = 0;
          for (const [country, count] of Object.entries(dist)) {
            const c = country.trim().toLowerCase();
            total += count as number;
            if (c === 'united states' || c === 'usa' || c === 'u.s.a.' || c === 'u.s.') us += count as number;
          }
          setCooConfidentDist({ us, total });
        }
      } catch { /* silently fail */ }
    })();
  }, []);

  // Fetch distinct COO values from backend (covers all 129K+ products)
  const [uniqueCoos, setUniqueCoos] = useState<string[]>([]);
  const [availableTiers, setAvailableTiers] = useState<TierInfo[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const [countriesRes, tiersRes] = await Promise.all([
          fetch(`${API_BASE}/metasupply/products/countries`, { credentials: 'include' }),
          fetch(`${API_BASE}/metasupply/products/tiers`, { credentials: 'include' }),
        ]);
        if (countriesRes.ok) {
          const data = await countriesRes.json();
          setUniqueCoos(data.countries || []);
        }
        if (tiersRes.ok) {
          const data = await tiersRes.json();
          setAvailableTiers(data.tiers || []);
        }
      } catch (e) {
        console.error('Error fetching filter options:', e);
      }
    })();
  }, []);

  const uniqueManufacturers = useMemo(() => {
    const set = new Set<string>();
    products.forEach(p => {
      if (p.manufacturer) set.add(p.manufacturer);
    });
    return Array.from(set).sort();
  }, [products]);

  const hasActiveFilters = filters.risk || filters.baa || filters.coo || filters.manufacturer || filters.tiers.length > 0 || filters.shortage;

  // Fetch products from backend with full server-side pagination, sorting, and filtering (A3.7)
  const fetchProducts = useCallback(async () => {
    setServerLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(pageSize));
      params.set('offset', String((currentPage - 1) * pageSize));
      params.set('sort_field', sortField);
      params.set('sort_dir', sortDir);
      if (filters.risk) params.set('risk', filters.risk);
      if (filters.baa) params.set('baa', filters.baa);
      if (filters.coo) params.set('coo', filters.coo);
      if (filters.manufacturer) params.set('manufacturer', filters.manufacturer);
      if (filters.tiers.length > 0) params.set('tier', filters.tiers.join(','));
      if (filters.shortage) params.set('shortage_status', filters.shortage);

      const response = await fetch(`${API_BASE}/metasupply/products?${params.toString()}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setServerProducts(data.products);
        setServerTotal(data.total ?? data.products.length);
        if (data.aggregates) {
          setServerAggregates(data.aggregates);
        }
        setInitialLoadDone(true);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setServerLoading(false);
    }
  }, [currentPage, pageSize, sortField, sortDir, filters]);

  // Fetch from server on every page/sort/filter change
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Reset to page 1 when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.risk, filters.baa, filters.coo, filters.manufacturer, filters.tiers, filters.shortage, sortField, sortDir]);

  // Products are already sorted and paginated by server — use directly
  const displayProducts = serverProducts;
  const displayTotal = serverTotal;
  const isLoading = loading || serverLoading;
  // No client-side sorting needed — server handles ORDER BY
  const paginatedProducts = displayProducts;

  const totalPages = Math.max(1, Math.ceil(displayTotal / pageSize));

  // Stat card values from server aggregates (A3.7)
  const totalCount = displayTotal;
  const highRiskCount = serverAggregates.high_risk_count;
  const miaConfidentCount = serverAggregates.mia_confident_count;
  const cooConfidentCount = serverAggregates.coo_confident_count;
  const cooConfidentPct = totalCount > 0 ? Math.round(cooConfidentCount / totalCount * 100) : 0;

  // Sync view mode with screen size when user hasn't manually overridden
  useEffect(() => {
    if (!viewOverridden) {
      setViewMode(isMobile ? 'card' : 'table');
    }
  }, [isMobile, viewOverridden]);

  const handleViewToggle = (mode: ViewMode) => {
    setViewMode(mode);
    setViewOverridden(true);
  };

  // Unified search state
  const query = unifiedSearchQuery.trim();
  const hasSearch = query.length > 0;
  const showGrouped = hasSearch && unifiedSearch;

  // Sort toggle handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <span style={{ color: T.textLight, fontSize: 10, marginLeft: 4 }}>{"\u2195"}</span>;
    return <span style={{ color: T.navy, fontSize: 10, marginLeft: 4 }}>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>;
  };

  // CSV export — fetches ALL matching products from server (not just current page) (A3.7)
  const [exporting, setExporting] = useState(false);
  const handleExportCSV = useCallback(async () => {
    setExporting(true);
    try {
      // Fetch all matching products (up to 200k) for export
      const params = new URLSearchParams();
      params.set('limit', '10000');
      params.set('offset', '0');
      params.set('sort_field', sortField);
      params.set('sort_dir', sortDir);
      if (filters.risk) params.set('risk', filters.risk);
      if (filters.baa) params.set('baa', filters.baa);
      if (filters.coo) params.set('coo', filters.coo);
      if (filters.manufacturer) params.set('manufacturer', filters.manufacturer);
      if (filters.tiers.length > 0) params.set('tier', filters.tiers.join(','));
      if (filters.shortage) params.set('shortage_status', filters.shortage);

      const response = await fetch(`${API_BASE}/metasupply/products?${params.toString()}`, {
        credentials: 'include',
      });
      if (!response.ok) return;
      const data = await response.json();
      const allProducts: Product[] = data.products;

      const csvHeaders = [
        'Product Name', 'NDC Code', 'Generic Name', 'Strength', 'Dosage Form',
        'Manufacturer', 'BAA Eligible', 'TAA Eligible',
        'Risk Level', 'Country of Origin', 'COO Tier', 'Shortage Status', 'Shortage Frequency',
        'Therapeutic Class', 'Last Updated',
      ];

      const escapeCSV = (val: string): string => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const rows = allProducts.map(p => [
        p.product_name || '',
        p.ndc_code || '',
        p.generic_name || '',
        p.strength || '',
        p.dosage_form || '',
        p.manufacturer || '',
        p.baa_eligible ? 'Yes' : 'No',
        p.taa_eligible ? 'Yes' : 'No',
        p.supply_chain_risk || '',
        p.country_of_origin || '',
        p.coo_scoring_tier ? normalizeCooTier(p.coo_scoring_tier).label : '',
        p.shortage_status || 'None',
        String(p.shortage_frequency ?? 0),
        p.therapeutic_class || '',
        p.last_updated ? new Date(p.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '',
      ].map(escapeCSV));

      const csvContent = [csvHeaders.map(escapeCSV).join(','), ...rows.map(r => r.join(','))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `products-export-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExporting(false);
    }
  }, [sortField, sortDir, filters]);

  // Select style helper

  // Filter pill style

  // Table header style
  const thStyle: React.CSSProperties = {
    padding: "12px 16px",
    fontSize: 11,
    fontWeight: 700,
    color: T.textLight,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    textAlign: "left",
    borderBottom: `2px solid ${T.border}`,
    whiteSpace: "nowrap",
    userSelect: "none",
  };

  // Pagination controls
  const renderPagination = () => {
    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, displayTotal);

    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return (
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 20px",
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusSm,
        boxShadow: T.shadow,
        flexWrap: "wrap",
        gap: 12,
      }}>
        <span style={{ fontSize: 13, color: T.textMid }}>
          Showing <strong>{startItem}</strong>{"\u2013"}<strong>{endItem}</strong> of <strong>{displayTotal.toLocaleString()}</strong> products
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            style={{
              padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`,
              background: currentPage <= 1 ? T.offWhite : T.white, color: currentPage <= 1 ? T.textLight : T.textDark,
              cursor: currentPage <= 1 ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600,
            }}
          >
            Prev
          </button>
          {pages.map((p, i) =>
            typeof p === 'string' ? (
              <span key={`ellipsis-${i}`} style={{ padding: "6px 4px", color: T.textLight, fontSize: 13 }}>...</span>
            ) : (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                style={{
                  padding: "6px 10px", borderRadius: 6, fontSize: 13, fontWeight: 600,
                  border: `1px solid ${p === currentPage ? T.navy : T.border}`,
                  background: p === currentPage ? T.navy : T.white,
                  color: p === currentPage ? T.white : T.textDark,
                  cursor: "pointer",
                  minWidth: 36,
                }}
              >
                {p}
              </button>
            )
          )}
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            style={{
              padding: "6px 12px", borderRadius: 6, border: `1px solid ${T.border}`,
              background: currentPage >= totalPages ? T.offWhite : T.white,
              color: currentPage >= totalPages ? T.textLight : T.textDark,
              cursor: currentPage >= totalPages ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 600,
            }}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Card view for a product (used in card mode on any screen size)
  const renderProductCard = (p: Product) => (
    <div
      key={p.id}
      onClick={() => loadProductDetail(p.id)}
      style={{
        background: T.white,
        border: `1px solid ${T.border}`,
        borderRadius: T.radius,
        padding: 16,
        cursor: "pointer",
        boxShadow: T.shadow,
        transition: "box-shadow 0.2s",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.textDark, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {p.product_name}
          </div>
          <div style={{ fontSize: 12, color: T.textMid }}>
            {p.manufacturer || "Unknown"}
          </div>
        </div>
        {p.coo_scoring_tier && (() => {
          const { label, bg, fg } = normalizeCooTier(p.coo_scoring_tier);
          return (
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8, background: bg, color: fg, whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 12 }}>
              {label}
            </span>
          );
        })()}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <RiskBadge risk={p.supply_chain_risk} />
        <ShortageBadge status={p.shortage_status || 'None'} T={T} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textLight }}>
        <span>COO: {p.country_of_origin || 'Unknown'}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {p.coo_scoring_tier && (() => {
            const { label, bg, fg } = normalizeCooTier(p.coo_scoring_tier);
            return (
              <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: bg, color: fg, whiteSpace: 'nowrap' }}>
                {label}
              </span>
            );
          })()}
          <span style={{ color: T.textLight }}><IconChevron /></span>
        </div>
      </div>
    </div>
  );

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Page Header + Search */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 16, flexDirection: isMobile ? "column" : "row" }}>
        <SectionHeader sub={
          hasSearch && unifiedSearch
            ? `${unifiedSearch.clinical_drugs.count + unifiedSearch.products.count + unifiedSearch.packagings.count} results for "${query}"`
            : `${displayTotal.toLocaleString()} products tracked`
        }>Products</SectionHeader>
        <div style={{ minWidth: isMobile ? "100%" : 320, maxWidth: isMobile ? "100%" : 420, width: "100%", position: "relative" }}>
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: T.textLight }}>
            <IconSearch />
          </span>
          <input
            value={unifiedSearchQuery}
            onChange={e => {
              setUnifiedSearchQuery(e.target.value);
              if (!e.target.value.trim()) {
                setUnifiedSearch(null);
                setProductFilter("");
              } else {
                setProductFilter(e.target.value);
              }
            }}
            placeholder="Search products, packagings, clinical drugs..."
            style={{
              width: "100%",
              padding: "12px 14px 12px 44px",
              borderRadius: T.radiusSm,
              border: `2px solid ${T.border}`,
              background: T.white,
              color: T.textDark,
              fontSize: 14,
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {isUnifiedSearching && (
            <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)" }}>
              <IconSpinner />
            </span>
          )}
        </div>
      </div>

      {/* Unified Search Loading */}
      {isUnifiedSearching && hasSearch && (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          padding: 60, background: T.white, borderRadius: T.radius,
          border: `1px solid ${T.border}`, boxShadow: T.shadow
        }}>
          <div style={{
            width: 48, height: 48, border: `3px solid ${T.border}`, borderTopColor: T.navy,
            borderRadius: "50%", animation: "spin 0.8s linear infinite", marginBottom: 16
          }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: T.navy, marginBottom: 4 }}>Searching...</div>
          <div style={{ fontSize: 13, color: T.textLight }}>Looking through products, packagings &amp; clinical drugs</div>
        </div>
      )}

      {/* Unified Search Grouped Results */}
      {!isUnifiedSearching && showGrouped ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Clinical Drugs group */}
          {unifiedSearch.clinical_drugs.results.length > 0 && (
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
              <div
                onClick={() => toggleSection('clinical_drugs')}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: "linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)",
                  cursor: "pointer", borderBottom: collapsedSections['clinical_drugs'] ? "none" : "1px solid #DDD6FE"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>\uD83E\uDDEC</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#4C1D95" }}>Clinical Drugs</div>
                    <div style={{ fontSize: 12, color: "#7C3AED" }}>{unifiedSearch.clinical_drugs.count} matches</div>
                  </div>
                </div>
                <div style={{ transform: collapsedSections['clinical_drugs'] ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#7C3AED" }}>
                  <IconChevron />
                </div>
              </div>
              {!collapsedSections['clinical_drugs'] && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {unifiedSearch.clinical_drugs.results.map((cd, i) => (
                    <div
                      key={cd.clinical_drug_id}
                      onClick={() => loadClinicalDrugDetail(cd.clinical_drug_id)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 20px", cursor: "pointer",
                        borderBottom: i < unifiedSearch.clinical_drugs.results.length - 1 ? `1px solid ${T.borderLight}` : "none",
                        background: T.white, transition: "background 0.15s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                      onMouseLeave={e => e.currentTarget.style.background = T.white}
                    >
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>{cd.name}</div>
                        <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>RxCUI: {cd.clinical_drug_id}</div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span style={{ padding: "4px 12px", borderRadius: 12, background: "#F5F3FF", color: "#7C3AED", fontSize: 12, fontWeight: 700 }}>
                          {cd.product_count} products
                        </span>
                        <span style={{ color: T.textLight }}><IconChevron /></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Products group */}
          {unifiedSearch.products.results.length > 0 && (
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
              <div
                onClick={() => toggleSection('products')}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
                  cursor: "pointer", borderBottom: collapsedSections['products'] ? "none" : "1px solid #BFDBFE"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>\uD83D\uDC8A</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1E40AF" }}>Products</div>
                    <div style={{ fontSize: 12, color: "#3B82F6" }}>{unifiedSearch.products.count} matches</div>
                  </div>
                </div>
                <div style={{ transform: collapsedSections['products'] ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#3B82F6" }}>
                  <IconChevron />
                </div>
              </div>
              {!collapsedSections['products'] && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {unifiedSearch.products.results.map((p, i) => (
                    <div
                      key={p.id}
                      onClick={() => loadProductDetail(p.id)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 20px", cursor: "pointer",
                        borderBottom: i < unifiedSearch.products.results.length - 1 ? `1px solid ${T.borderLight}` : "none",
                        background: T.white, transition: "background 0.15s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                      onMouseLeave={e => e.currentTarget.style.background = T.white}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 600, color: T.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {p.product_name}
                        </div>
                        <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>
                          {p.manufacturer || "Unknown"} &bull; NDC: {p.ndc_code}
                          {p.strength && <span> &bull; {p.strength}</span>}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {(() => { const _coo = p.country_of_origin; return _coo && _coo !== "Unknown" ? (
                          <span style={{
                            padding: "3px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600,
                            background: _coo === "United States" ? T.successBg : _coo === "China" ? T.dangerBg : T.warningBg,
                            color: _coo === "United States" ? T.success : _coo === "China" ? T.danger : T.warning
                          }}>
                            {_coo}
                          </span>
                        ) : null; })()}
                        {p.coo_scoring_tier && (() => {
                          const upper = p.coo_scoring_tier.toUpperCase();
                          const isHigh = upper === 'HIGH' || upper === 'NEAR_CERTAIN';
                          const isMod = upper === 'MODERATE' || upper === 'MODERATE_HIGH';
                          const label = isHigh ? 'High' : isMod ? 'Medium' : 'Low';
                          const color = isHigh ? '#22C55E' : isMod ? '#F59E0B' : '#EF4444';
                          const bg = isHigh ? '#22C55E18' : isMod ? '#F59E0B18' : '#EF444418';
                          return (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8, background: bg, color, whiteSpace: 'nowrap' }}>
                              {label}
                            </span>
                          );
                        })()}
                        <span style={{ color: T.textLight }}><IconChevron /></span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Packagings group */}
          {unifiedSearch.packagings.results.length > 0 && (
            <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: "hidden", boxShadow: T.shadow }}>
              <div
                onClick={() => toggleSection('packagings')}
                style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "16px 20px", background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)",
                  cursor: "pointer", borderBottom: collapsedSections['packagings'] ? "none" : "1px solid #A7F3D0"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 20 }}>\uD83D\uDCE6</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#065F46" }}>Packagings</div>
                    <div style={{ fontSize: 12, color: "#059669" }}>{unifiedSearch.packagings.count} matches</div>
                  </div>
                </div>
                <div style={{ transform: collapsedSections['packagings'] ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.2s", color: "#059669" }}>
                  <IconChevron />
                </div>
              </div>
              {!collapsedSections['packagings'] && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {unifiedSearch.packagings.results.map((pkg, i) => (
                    <div
                      key={pkg.id}
                      onClick={() => loadProductDetail(pkg.product_id)}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "14px 20px", cursor: "pointer",
                        borderBottom: i < unifiedSearch.packagings.results.length - 1 ? `1px solid ${T.borderLight}` : "none",
                        background: T.white, transition: "background 0.15s"
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                      onMouseLeave={e => e.currentTarget.style.background = T.white}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: T.navy, fontFamily: "'IBM Plex Mono', monospace" }}>{pkg.package_ndc}</span>
                          {pkg.nadac_per_unit && (
                            <span style={{ padding: "2px 8px", borderRadius: 8, background: T.successBg, color: T.success, fontSize: 12, fontWeight: 700 }}>
                              ${pkg.nadac_per_unit.toFixed(4)}/{pkg.nadac_pricing_unit || "unit"}
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: T.textLight, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {pkg.product_name} &bull; {pkg.manufacturer || "Unknown"}
                        </div>
                        {pkg.description && (
                          <div style={{ fontSize: 11, color: T.textLight, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {pkg.description}
                          </div>
                        )}
                      </div>
                      <span style={{ color: T.textLight }}><IconChevron /></span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* No results */}
          {unifiedSearch.clinical_drugs.results.length === 0 &&
           unifiedSearch.products.results.length === 0 &&
           unifiedSearch.packagings.results.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: T.textLight, background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}` }}>
              No results found for &ldquo;{query}&rdquo;. Try a different search term.
            </div>
          )}
        </div>

      ) : !isUnifiedSearching && !hasSearch ? (
        /* Main Products View (no search) */
        isLoading && !displayProducts.length ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}><IconSpinner /></div>
        ) : (
          <>
            {/* Summary Stat Cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap: 16,
            }}>
              <StatCard
                title="# of Drugs"
                value={totalCount.toLocaleString()}
                color={T.navy}
                accent="#000000"
                icon={<IconDatabase />}
              />
              <StatCard
                title="High Risk"
                value={highRiskCount.toLocaleString()}
                color={T.danger}
                accent="#000000"
                icon={<IconAlert />}
              />
              <StatCard
                title="Shortages"
                value={serverAggregates ? serverAggregates.shortage_product_count.toLocaleString() : '0'}
                color={T.warning}
                accent="#000000"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>}
              />
              {/* COO Confidence Distribution Tile */}
              <div style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderTop: '3px solid #000000',
                borderRadius: T.radius,
                boxShadow: T.shadow,
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ color: T.navy }}><IconShield /></span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textLight }}>COO Confidence</span>
                </div>
                {([
                  { label: 'HIGH', count: serverAggregates.coo_high_count, color: '#22C55E', key: 'HIGH' },
                  { label: 'MODERATE', count: serverAggregates.coo_moderate_count, color: '#F59E0B', key: 'MODERATE' },
                  { label: 'LOW', count: serverAggregates.coo_low_count, color: '#EF4444', key: 'LOW' },
                ] as const).map(tier => {
                  const distTotal = serverAggregates.coo_high_count + serverAggregates.coo_moderate_count + serverAggregates.coo_low_count;
                  const pct = distTotal > 0 ? ((tier.count / distTotal) * 100).toFixed(1) : '0.0';
                  const isActive = globalFilters.cooConfidence.includes(tier.key);
                  return (
                    <div
                      key={tier.key}
                      onClick={() => {
                        if (isActive) {
                          setGlobalFilter('cooConfidence', globalFilters.cooConfidence.filter((t: string) => t !== tier.key));
                        } else {
                          setGlobalFilter('cooConfidence', [...globalFilters.cooConfidence, tier.key]);
                        }
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                        background: isActive ? `${tier.color}18` : 'transparent',
                        border: isActive ? `1.5px solid ${tier.color}` : '1.5px solid transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: tier.color,
                        }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.textDark }}>{tier.label}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 500, color: T.textLight }}>
                        {tier.count.toLocaleString()} ({pct}%)
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* View Mode Toolbar */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              padding: "12px 20px",
              background: T.white,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              boxShadow: T.shadow,
            }}>
              <div style={{ display: "flex", borderRadius: T.radiusSm, border: `1px solid ${T.border}`, overflow: "hidden" }}>
                <button
                  onClick={() => handleViewToggle('table')}
                  title="Table view"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "6px 10px", border: "none", cursor: "pointer",
                    background: viewMode === 'table' ? T.navy : T.white,
                    color: viewMode === 'table' ? T.white : T.textLight,
                    transition: "all 0.15s",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                    <line x1="3" y1="9" x2="21" y2="9" />
                    <line x1="3" y1="15" x2="21" y2="15" />
                    <line x1="9" y1="3" x2="9" y2="21" />
                  </svg>
                </button>
                <button
                  onClick={() => handleViewToggle('card')}
                  title="Card view"
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "center",
                    padding: "6px 10px", border: "none", borderLeft: `1px solid ${T.border}`,
                    cursor: "pointer",
                    background: viewMode === 'card' ? T.navy : T.white,
                    color: viewMode === 'card' ? T.white : T.textLight,
                    transition: "all 0.15s",
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Data Table / Card List (toggle) */}
            {viewMode === 'card' ? (
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 16,
              }}>
                {isLoading && (
                  <div style={{ display: "flex", justifyContent: "center", padding: 40, gridColumn: "1 / -1" }}><IconSpinner /></div>
                )}
                {!isLoading && paginatedProducts.map(p => renderProductCard(p))}
                {!isLoading && paginatedProducts.length === 0 && (
                  <div style={{ padding: 40, textAlign: "center", color: T.textLight, background: T.white, borderRadius: T.radius, border: `1px solid ${T.border}`, gridColumn: "1 / -1" }}>
                    {hasActiveFilters
                      ? "No products match the current filters."
                      : "No products found. Use Data Ingestion to add products."}
                  </div>
                )}
              </div>
            ) : (
              <div style={{
                background: T.white,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                boxShadow: T.shadow,
                overflow: "hidden",
              }}>
                {isLoading && (
                  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}><IconSpinner /></div>
                )}
                {!isLoading && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: T.offWhite }}>
                          <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('name')}>
                            NDC # <SortIcon field="name" />
                          </th>
                          <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('name')}>
                            Drug <SortIcon field="name" />
                          </th>
                          <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('manufacturer')}>
                            Manufacturer <SortIcon field="manufacturer" />
                          </th>
                          <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('name')}>COO</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>COO Tier</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>Shortage</th>
                          <th style={{ ...thStyle, textAlign: "center" }}>Shortage Freq.</th>
                          <th style={{ ...thStyle, cursor: "pointer", textAlign: "center" }} onClick={() => handleSort('risk')}>
                            Risk <SortIcon field="risk" />
                          </th>
                          <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleSort('updated')}>
                            Updated <SortIcon field="updated" />
                          </th>
                          <th style={{ ...thStyle, width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedProducts.map((p, idx) => (
                          <tr
                            key={p.id}
                            onClick={() => loadProductDetail(p.id)}
                            style={{
                              cursor: "pointer",
                              borderBottom: idx < paginatedProducts.length - 1 ? `1px solid ${T.borderLight}` : "none",
                              transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = T.offWhite}
                            onMouseLeave={e => e.currentTarget.style.background = T.white}
                          >
                            <td style={{ padding: "14px 16px" }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: T.navy, fontFamily: "'JetBrains Mono', 'IBM Plex Mono', monospace", letterSpacing: "-0.01em" }}>
                                {p.ndc_code || '—'}
                              </span>
                            </td>
                            <td style={{ padding: "14px 16px", maxWidth: 280 }}>
                              <div style={{ fontSize: 14, fontWeight: 600, color: T.textDark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {p.product_name}
                              </div>
                              {p.strength && (
                                <div style={{ fontSize: 12, color: T.textLight, marginTop: 2 }}>{p.strength}</div>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span style={{ fontSize: 13, color: T.textMid }}>{p.manufacturer || "Unknown"}</span>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span style={{
                                fontSize: 13, fontWeight: 600,
                                color: (p.country_of_origin || '') === "United States" ? T.success
                                  : (p.country_of_origin || '') === "China" ? T.danger
                                  : T.textMid,
                              }}>
                                {p.country_of_origin || "Unknown"}
                              </span>
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {p.coo_scoring_tier ? (() => {
                                const { label, bg, fg } = normalizeCooTier(p.coo_scoring_tier);
                                return (
                                  <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8, background: bg, color: fg, whiteSpace: 'nowrap' }}>
                                    {label}
                                  </span>
                                );
                              })() : (
                                <span style={{ fontSize: 12, color: T.textLight }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              <ShortageBadge status={p.shortage_status || 'None'} T={T} />
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              <span style={{
                                fontSize: 13, fontWeight: 700,
                                color: (p.shortage_frequency ?? 0) > 0 ? T.danger : T.textLight,
                              }}>
                                {p.shortage_frequency ?? 0}
                              </span>
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              <RiskBadge risk={p.supply_chain_risk} />
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <span style={{ fontSize: 12, color: T.textLight }}>
                                {p.last_updated
                                  ? new Date(p.last_updated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  : '—'}
                              </span>
                            </td>
                            <td style={{ padding: "14px 16px", color: T.textLight }}>
                              <IconChevron />
                            </td>
                          </tr>
                        ))}
                        {paginatedProducts.length === 0 && (
                          <tr>
                            <td colSpan={10} style={{ padding: 40, textAlign: "center", color: T.textLight }}>
                              {hasActiveFilters
                                ? "No products match the current filters."
                                : "No products found. Use Data Ingestion to add products."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {displayTotal > pageSize && renderPagination()}
          </>
        )
      ) : null}
    </div>
  );
}
